import { getLong, logicalKeys, setLong, type Storage } from '../platform/storage'
import { yearMonthOf } from './keys'
import { rememberEvent, rememberWindow } from './outbox'

/**
 * Что происходило внутри окна бодрствования.
 *
 * История начала окон уже есть, но для специалиста по сну главное не «когда проснулся»,
 * а что было дальше: сколько на самом деле длилась каждая фаза, переключили её вовремя
 * или раньше срока, дошли ли до ритуала. Из таких записей собирается любой отчёт,
 * из одного лишь начала окна — нет.
 *
 * Записи только дописываются и никогда не правятся, поэтому слияние тривиально:
 * у каждой свой номер, повтор ничего не портит. Хранятся помесячно, как заметки
 * и история, и под своим ключом — формат `windows_<месяц>` менять нельзя,
 * его читают часы и виджет Android.
 */

export const EVENTS_PREFIX = 'events_'

/**
 * `phase` — перешли к фазе. `window_end` — окно закончилось, чем именно — в `reason`.
 * `night_wake` / `night_wake_end` — ночное пробуждение: «Проснулся» между укладыванием
 * и началом дня. Не окно: без фаз, будильников и пушей, но для консультантов по сну
 * это важнейшая запись (решение 28.09.2026). `weight` — взвешивание, граммы в `grams`:
 * тоже событие во времени, поэтому живёт здесь, а не в заметках.
 */
export type EventKind = 'phase' | 'window_end' | 'night_wake' | 'night_wake_end' | 'weight'

/**
 * Чем кончилось окно: уснул, не уснул, человек сбросил его сам — или никто не ответил.
 *
 * `asleep` и `awake` — это ответ на вопрос «Уснул?». Окно кончается в обоих случаях:
 * бодрствование позади, вопрос только в том, чем оно завершилось. `unanswered` — окно
 * закрылось само через два часа после плана; в сводке дня такой сон считается примерно.
 */
export type EndReason = 'asleep' | 'awake' | 'reset' | 'unanswered'

export interface DayEvent {
  /** Номер записи: момент её появления. Уникален на устройстве и не меняется. */
  id: number
  kind: EventKind
  /** Когда произошло. */
  at: number
  /** К какому окну относится: момент нажатия «Проснулся» (у ночного пробуждения — его начало). */
  window: number
  /** Для фазы: её номер по порядку. */
  index?: number
  /** Для фазы: название, чтобы отчёт читался без обращения к программе. */
  title?: string
  /** Для фазы: сколько минут она должна была длиться по расписанию. */
  planned?: number
  /** Для конца окна или ночного пробуждения: уснул, сбросили или никто не ответил. */
  reason?: EndReason
  /** Для взвешивания: вес в граммах. */
  grams?: number
  /**
   * Окно бодрствования вписали руками в календаре, а не отсчитал таймер (просьба 12.10.2026).
   * Такая запись — всё окно целиком: `window` — начало, `at` — конец. Начала в истории окон
   * у неё нет: история пишется нажатием «Проснулся», а его здесь не было.
   */
  manual?: boolean
  /**
   * Запись удалили. Остаётся меткой, как у заметок: иначе сервер о удалении не узнаёт
   * и присылает запись обратно. Удалять можно взвешивания и окна, вписанные руками:
   * ошибку ввода исправляют. Фазы и концы окон от таймера — история, её не трогаем.
   */
  deleted?: boolean
  /**
   * Ручная запись заменяет окно таймера с этим началом (правка времени, просьба 20.10.2026).
   * Такое начало из истории больше не показывается — и после удаления самой записи тоже:
   * удаление необратимо. Саму историю не правим: её читают часы и виджет Android, а в
   * обмене нет удаления начал.
   */
  replaces?: number
  /** Где спал: быстрая метка («на руках», «в кроватке»…). */
  place?: string
  /** Комментарий родителя ко сну: запись о конце окна — это и есть момент засыпания. */
  note?: string
  /**
   * Комментарий к самому бодрствованию. Отдельное поле: раньше сон и бодрствование делили
   * `note`, и правка на одной вкладке стирала текст на другой (жалоба 21.10.2026).
   */
  wakeNote?: string
}

function key(at: number): string {
  return `${EVENTS_PREFIX}${yearMonthOf(at)}`
}

function parse(raw: string | null): DayEvent[] {
  if (raw === null) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (e): e is DayEvent =>
          typeof e?.id === 'number' && typeof e?.at === 'number' && typeof e?.window === 'number' && typeof e?.kind === 'string',
      )
      .map((e) => (e.deleted === true ? { ...e, deleted: true } : e))
  } catch {
    return []
  }
}

export async function loadEvents(storage: Storage): Promise<DayEvent[]> {
  const all: DayEvent[] = []
  for (const k of logicalKeys(await storage.keys())) {
    if (k.startsWith(EVENTS_PREFIX)) all.push(...parse(await getLong(storage, k)))
  }
  return all.sort((a, b) => a.at - b.at)
}

/** Помечает запись удалённой. Метка необратима: удалённое где-то одно удалено везде. */
export async function markEventDeleted(storage: Storage, event: DayEvent): Promise<void> {
  const k = key(event.at)
  const existing = parse(await getLong(storage, k))
  const updated = existing.some((e) => e.id === event.id)
    ? existing.map((e) => (e.id === event.id ? { ...e, deleted: true } : e))
    : [...existing, { ...event, deleted: true }]
  await setLong(storage, k, JSON.stringify(updated.sort((a, b) => a.at - b.at)))
  // Удаление старой записи — изменение, о котором устройство само сообщает серверу
  await rememberEvent(storage, event.id)
}

/**
 * Переписывает свою же ручную запись на месте, сохраняя её номер.
 *
 * Правка времени окна раньше всегда делала две вещи: прежнюю запись помечала удалённой,
 * новую дописывала с новым номером. Восемь сохранений подряд оставляли восемь записей, семь
 * из них удалённых — у жены заказчика ровно так и вышло 29.10.2026. Своя ручная правка
 * ничего не прячет и не теряет: её можно просто переписать.
 *
 * Запись могла переехать в другой месяц (правка времени через полночь), поэтому старую
 * убираем из её месяца, а новую кладём в свой.
 */
export async function replaceEvent(storage: Storage, event: DayEvent, было: number): Promise<void> {
  const старыйКлюч = key(было)
  const новыйКлюч = key(event.at)
  if (старыйКлюч === новыйКлюч) {
    const existing = parse(await getLong(storage, новыйКлюч))
    const updated = existing.map((e) => (e.id === event.id ? event : e)).sort((a, b) => a.at - b.at)
    await setLong(storage, новыйКлюч, JSON.stringify(updated))
  } else {
    const старые = parse(await getLong(storage, старыйКлюч)).filter((e) => e.id !== event.id)
    await setLong(storage, старыйКлюч, JSON.stringify(старые))
    const новые = [...parse(await getLong(storage, новыйКлюч)).filter((e) => e.id !== event.id), event].sort((a, b) => a.at - b.at)
    await setLong(storage, новыйКлюч, JSON.stringify(новые))
  }
  await rememberEvent(storage, event.id)
  await отметитьОкноИзменённым(storage, event)
}

/** Дописывает запись. Повтор с тем же номером ничего не меняет. */
export async function addEvent(storage: Storage, event: DayEvent): Promise<void> {
  const k = key(event.at)
  const existing = parse(await getLong(storage, k))
  if (existing.some((e) => e.id === event.id)) return
  const updated = [...existing, event].sort((a, b) => a.at - b.at)
  await setLong(storage, k, JSON.stringify(updated))
  await rememberEvent(storage, event.id)
  await отметитьОкноИзменённым(storage, event)
}

/**
 * Появился конец окна — изменилось само окно, а не только журнал: шлём окно заново.
 *
 * С обменом только изменениями окно уезжало на сервер при старте, с пустым концом, и
 * больше не менялось: конец ехал отдельной записью журнала. На сервере окно так и
 * оставалось открытым — журнал его закрыл, таблица окон о конце не знала (28.10.2026).
 *
 * Только появление, не удаление: правка времени окна сначала убирает старый конец и тут же
 * пишет новый — помечает его появление. А «Убрать окно» конца на сервере не меняет, и гонять
 * туда-сюда строку окна незачем: едут пометки об удалении, а не история.
 */
async function отметитьОкноИзменённым(storage: Storage, event: DayEvent): Promise<void> {
  if (event.kind !== 'window_end' || event.deleted === true) return
  await rememberWindow(storage, event.window)
}

/** Кладёт записи, пришедшие с сервера, не трогая те, что уже есть. */
export async function mergeEvents(storage: Storage, incoming: DayEvent[]): Promise<void> {
  const byMonth = new Map<string, Map<number, DayEvent>>()
  for (const k of logicalKeys(await storage.keys())) {
    if (k.startsWith(EVENTS_PREFIX)) byMonth.set(k, new Map(parse(await getLong(storage, k)).map((e) => [e.id, e])))
  }
  for (const event of incoming) {
    const k = key(event.at)
    const map = byMonth.get(k) ?? new Map<number, DayEvent>()
    const known = map.get(event.id)
    // Метка удаления побеждает в любую сторону; остальное у известной записи не меняется
    if (known === undefined) map.set(event.id, event)
    else if (event.deleted === true && known.deleted !== true) map.set(event.id, { ...known, deleted: true })
    byMonth.set(k, map)
  }
  for (const [k, map] of byMonth) {
    await setLong(storage, k, JSON.stringify([...map.values()].sort((a, b) => a.at - b.at)))
  }
}

/**
 * Начала окон таймера, заменённые ручной записью: в списках их нет. Считается по всем
 * записям, включая удалённые: убрать окно — это тоже заменить его, пустотой.
 */
export function hiddenStarts(events: DayEvent[]): Set<number> {
  const out = new Set<number>()
  for (const e of events) if (typeof e.replaces === 'number') out.add(e.replaces)
  return out
}

/** Когда окно закрылось, если это известно. Нужно, чтобы отдать серверу конец окна. */
export function windowEnds(events: DayEvent[]): Map<number, number> {
  const ends = new Map<number, number>()
  for (const e of events) {
    if (e.kind === 'window_end') ends.set(e.window, e.at)
  }
  return ends
}
